import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useEventListener from '@use-it/event-listener';
import {
    has,
    isArray,
    isEmpty,
    isNil,
    isNull,
    isObject,
    mapValues,
    omit,
    upperFirst,
} from 'lodash-es';
import { useObservable, useObservableState, useSubscription } from 'observable-hooks';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { SwarmType } from '../../../enums';
import warning from '../../../utils/logger';
import { NotImplementedError } from '../../../utils/errors';
import { setNamingConventionsToGuideline } from '../../../utils/naming-conventions';
import { confirm, ConfirmType } from '../../confirm/Confirm';
import Button, { ButtonContainer } from '../../form/Button';
import { NOTIFICATION_VARIANT, useNotifications } from '../../notifications/Notifications';
import Form from '../../reactForm/Form';
import GraphQLDataLink from '../../reactForm/GraphQLDataLink';
import { Layout } from '../../reactForm/Layout';
import { OVERRIDES_NAME, useWatchObservable } from '../../reactForm/utils';
import { BasePage } from '../BasePage';
import { hasAtLeastPermission, PERMISSION, requirePermission } from '../sharing/utils';
import { ConfigHelper, ProjectConfigHelper } from './configHelper';
import { ConfigurationWarning } from './ConfigurationWarning';
import { NyDepConfirmation } from './NyDepConfirmation';
import { NyDepDeviceRegistrationListener } from './NyDepDeviceRegistrationListener';
import { SwarmFormController } from './form-controller';
import { ActivateSubscription } from './activateSubscription';
import { MeasuringPointFloatingPanels } from './MeasuringPointFloatingPanels';
import { createEditUrl, createQueriesForSwarmType, usePageTitle } from './utils';
import { SaveLoadingButton } from '../../reactForm/SaveLoadingButton';
import { getFieldOptionLabel } from './option-utils';

// Fields a measuring point never takes from its project, so they cannot be overridden either. The
// permission is the user's access to this entity, not a setting it inherits.
const notInheritedFields = [
    'id',
    'name',
    'deviceName',
    'location',
    'project',
    'privateNotes',
    'permission',
];

function removeTypeName(object) {
    return omit(object, ['__typename']);
}

function removeTypeNameRecursive(incoming) {
    if (isArray(incoming)) {
        return incoming.map((entry) => removeTypeNameRecursive(entry));
    }

    if (isObject(incoming)) {
        return mapValues(removeTypeName(incoming), (value) => removeTypeNameRecursive(value));
    }

    return incoming;
}

function createAddAnotherSuccessMessage(name, editUrl) {
    const placeholder = '{measuringPointName}';
    const message = gettext('MEASURING_POINT_ADDED_ADD_ANOTHER');
    const [beforeName, afterName] = message.split(placeholder);

    return (
        <>
            {beforeName}
            <a href={editUrl} className="font-semibold underline">
                {name}
            </a>
            {afterName}
        </>
    );
}

function DetachSensorHandler({ detachButtonRef, swarmType }) {
    const { setValue, watch } = useFormContext();

    if (detachButtonRef.current) {
        // Remove the Detach button if the deviceName field is empty.
        detachButtonRef.current.style.display = watch('deviceName') ? null : 'none';
    }

    const emptyDeviceName = useCallback(() => {
        setValue('deviceName', null);
        setTimeout(() => {
            confirm({
                message: interpolate(gettext('SWARM_DECOUPLED_AFTER_SAVE'), [
                    upperFirst(swarmType),
                ]),
                type: ConfirmType.INFO,
            });
        }, 1);
    }, [setValue, swarmType]);

    useEventListener('click', emptyDeviceName, detachButtonRef.current);

    return null;
}

DetachSensorHandler.propTypes = {
    detachButtonRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    swarmType: PropTypes.oneOf(Object.values(SwarmType)).isRequired,
};

function UpdateNamingConventions() {
    const guideLine$ = useWatchObservable('guideLine');
    useSubscription(guideLine$, (guideLine) => {
        guideLine && setNamingConventionsToGuideline(guideLine);
    });

    return null;
}

function translateFieldAndOption(formSchema, fieldName, optionName) {
    const field = formSchema.fields.getByName(fieldName);
    return `${field.label} ${getFieldOptionLabel(field, optionName)}`;
}

function DisabledFeaturesWarning({ configHelper, formSchema }) {
    const unsupportedFeatures$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([swarmFeatures$, incomingFormSchema]) =>
                    combineLatest([swarmFeatures$, of(incomingFormSchema)])
                ),
                // Map the incoming 'internal' config into a 'public' config.
                map(([disabledFeatures, incomingFormSchema]) =>
                    disabledFeatures.flatMap(({ deviceName, unavailableFeatures }) =>
                        unavailableFeatures
                            .filter(({ showBanner }) => showBanner)
                            .map(({ field, option }) => [
                                deviceName,
                                translateFieldAndOption(incomingFormSchema, field, option),
                            ])
                    )
                ),
                catchError((error) => {
                    warning(error);
                    return of([]);
                })
            ),
        [configHelper.swarmFeatures$, formSchema]
    );

    const unsupportedFeatures = useObservableState(unsupportedFeatures$);

    const { addNotification } = useNotifications();

    useEffect(() => {
        if (isEmpty(unsupportedFeatures)) {
            return undefined;
        }

        const { remove } = addNotification({
            message: (
                <>
                    <h1 className="text-xl">{gettext('UPDATE_YOUR_SWARMS')}</h1>
                    <ul>
                        {unsupportedFeatures.map(([deviceName, feature]) => (
                            <li key={[deviceName, feature]}>
                                {`${interpolate(
                                    gettext('FEATURE_NOT_SUPPORTED_BY'),
                                    { feature },
                                    true
                                )} ${deviceName}`}
                            </li>
                        ))}
                    </ul>
                    <p>{gettext('FIRMWARE_UPDATE_NOTIFICATION')}</p>
                </>
            ),
            variant: NOTIFICATION_VARIANT.ERROR,
        });

        return remove;
    }, [unsupportedFeatures, addNotification, formSchema]);

    return null;
}
DisabledFeaturesWarning.propTypes = {
    configHelper: PropTypes.instanceOf(ConfigHelper).isRequired,
    formSchema: PropTypes.object.isRequired,
};

function DeviceNameCorrection({ configHelper }) {
    const { setValue } = useFormContext();

    useSubscription(configHelper.deviceNameCorrection$, (nameCorrection) => {
        setValue('deviceName', nameCorrection);
    });

    return null;
}
DeviceNameCorrection.propTypes = {
    configHelper: PropTypes.instanceOf(ConfigHelper).isRequired,
};

function PermissionChecker({ setShowSaveButton }) {
    const fieldValues$ = useWatchObservable(['permission', 'archived']);
    useSubscription(fieldValues$, ({ permission, archived }) => {
        permission &&
            setShowSaveButton(hasAtLeastPermission(permission, PERMISSION.FullAccess) && !archived);
    });

    return null;
}
PermissionChecker.propTypes = {
    setShowSaveButton: PropTypes.func.isRequired,
};

function SwarmTypeUrlCorrection({ id, swarmType, isProject }) {
    const navigate = useNavigate();
    const swarmType$ = useWatchObservable('swarmType');

    useSubscription(swarmType$, (swarmTypeFromApi) => {
        if (isNil(swarmTypeFromApi)) {
            return;
        }

        // We query the API to determine the swarm type associated with the
        // given ID. If it doesn't correspond to the swarm type in the URL,
        // we courteously auto-redirect the user to the correct URL.
        if (swarmTypeFromApi !== swarmType) {
            navigate(createEditUrl(id, swarmTypeFromApi, isProject));
        }
    });

    return null;
}
SwarmTypeUrlCorrection.propTypes = {
    id: PropTypes.number.isRequired,
    swarmType: PropTypes.oneOf(Object.values(SwarmType)).isRequired,
    isProject: PropTypes.bool.isRequired,
};

class GenericSwarmFormController extends SwarmFormController {
    constructor(config) {
        super(config);

        this.notInheritedFields = [...notInheritedFields, ...this.createReadOnlyFields()];

        // Will be set by `GraphQLDataLink`.
        this.overriddenOnSubmitFunction = null;

        this.queries = createQueriesForSwarmType(this.swarmType)[this.isProject];
    }

    storeConfig({ id, ...config }) {
        this.id = id;
        super.storeConfig(config);
    }

    createConfigHelper() {
        return this.isProject ? new ProjectConfigHelper(this) : new ConfigHelper(this);
    }

    createReadOnlyFields() {
        return this.layoutHelper.fields
            .filter((field) => field.readOnly)
            .map((field) => field.name);
    }

    createLayoutTree() {
        const tree = super.createLayoutTree();

        // Private notes are rendered in the floating side panel, not in the
        // normal form layout, so we remove that group from the main tree.
        if (tree.groups.some((group) => group.name === 'privateNotes')) {
            tree.groups.removeByName('privateNotes');
        }

        return tree;
    }

    getNotInheritedFields() {
        return this.notInheritedFields;
    }

    isOverridable$(fieldName) {
        return this.parentValues$.pipe(
            // Only a loaded project's own fields can be overridden.
            map(
                (parentValues) =>
                    !isNull(parentValues) &&
                    has(parentValues, fieldName) &&
                    !this.notInheritedFields.includes(fieldName)
            )
        );
    }

    setOnSubmitFunction(onSubmitFunction) {
        this.overriddenOnSubmitFunction = onSubmitFunction;
    }

    onSubmitFunction(values, event, submissionOptions) {
        if (!this.overriddenOnSubmitFunction) {
            throw new NotImplementedError(
                'GenericSwarmFormController requires onSubmitFunction to be set via setOnSubmitFunction.'
            );
        }

        return this.overriddenOnSubmitFunction(values, event, submissionOptions);
    }

    async triggerSubmit(event, submissionOptions = {}) {
        const values = this.rhfProps.getValues();

        // Editing requires `FULL_ACCESS`.
        if ('id' in values && !hasAtLeastPermission(values.permission, PERMISSION.FullAccess)) {
            event.preventDefault();
            return;
        }

        await super.triggerSubmit(event, submissionOptions);
    }

    convertDataIntoFormValues(data) {
        const formValues = omit(removeTypeNameRecursive(data), ['overriddenFields']);

        if (data.overriddenFields) {
            formValues[OVERRIDES_NAME] = Object.fromEntries(
                data.overriddenFields.map((name) => [name, true])
            );
        }

        return this.convertConfigValues(formValues);
    }
}

// Links to the project loaded into the form. A reference-only project is named on this page, but it
// has no page of its own for the user to open.
function OpenProjectButton({ formController, swarmType }) {
    const project = useObservableState(formController.parentValues$);

    if (
        !project ||
        !hasAtLeastPermission(
            requirePermission(project.permission, `project "${project.id}"`),
            PERMISSION.View
        )
    ) {
        return null;
    }

    return (
        <Button
            href={createEditUrl(project.id, swarmType, true)}
            target="_blank"
            rel="noopener noreferrer"
            variant="neutral"
            className="flex items-center gap-2 rounded text-base"
        >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            {gettext('OPEN_PROJECT')}
        </Button>
    );
}
OpenProjectButton.propTypes = {
    formController: PropTypes.object.isRequired,
    swarmType: PropTypes.string.isRequired,
};

function GenericForm({ swarmType, isProject }) {
    const { id: rawId } = useParams();
    const { t } = useTranslation();

    const id = parseInt(rawId, 10);
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { addNotification } = useNotifications();

    const formController = GenericSwarmFormController.use(swarmType, isProject, { id });

    const title = usePageTitle(swarmType, isProject, isEditMode);

    const detachButtonRef = useRef();

    // Show the save button by default when in create mode. Hide the save button by default
    // when in edit mode. When in edit mode, as soon as the query containing the measuring
    // point or project permissions comes in, we decide if we want to show it.
    const [showSaveButton, setShowSaveButton] = useState(!isEditMode);

    const [searchParams] = useSearchParams();
    const requestedProjectId = searchParams.get('project');

    const InjectableComponent = useCallback(
        ({ showSaveButton: canEditConfiguration }) => (
            <>
                <PermissionChecker setShowSaveButton={setShowSaveButton} />
                <SwarmTypeUrlCorrection id={id} swarmType={swarmType} isProject={isProject} />
                <UpdateNamingConventions />
                {!isProject && (
                    <DetachSensorHandler detachButtonRef={detachButtonRef} swarmType={swarmType} />
                )}
                <DisabledFeaturesWarning
                    configHelper={formController.configHelper}
                    formSchema={formController.formSchema}
                />
                {!isProject && <DeviceNameCorrection configHelper={formController.configHelper} />}
                {<ActivateSubscription configHelper={formController.configHelper} />}
                <MeasuringPointFloatingPanels
                    showAlarmPreview={swarmType === SwarmType.Vibration}
                    canEditConfiguration={canEditConfiguration}
                />
                <ConfigurationWarning formController={formController} />
                <NyDepConfirmation formController={formController} />
                {!isProject && <NyDepDeviceRegistrationListener formController={formController} />}
            </>
        ),
        [detachButtonRef, formController, id, isProject, swarmType]
    );

    useSubscription(
        formController.onSubmitSuccessful$,
        ({ id: idFromAPI, addAnotherAfterSave, entityName, warnings = [] }) => {
            const editUrl = createEditUrl(idFromAPI, swarmType, isProject);

            if (!isEditMode) {
                if (addAnotherAfterSave && !isProject) {
                    const projectId = formController.rhfProps.getValues('project');
                    const createUrl = projectId
                        ? `/measuring_point/create/${swarmType}?project=${encodeURIComponent(
                              projectId
                          )}`
                        : `/measuring_point/create/${swarmType}`;

                    navigate(createUrl, {
                        replace: true,
                        state: { formInstanceKey: idFromAPI },
                    });
                } else {
                    navigate(editUrl);
                }
            }

            addNotification({
                message:
                    addAnotherAfterSave && !isProject
                        ? createAddAnotherSuccessMessage(entityName, editUrl)
                        : gettext('SAVED_SUCCESSFULLY'),
                variant: NOTIFICATION_VARIANT.SUCCESS,
                scrollToTop: true,
            });

            warnings.forEach((message) => {
                addNotification({ message, variant: NOTIFICATION_VARIANT.WARNING });
            });
        }
    );

    useEffect(() => {
        const deviceName = searchParams.get('SWARM');

        if (deviceName) {
            formController.rhfProps.setValue('deviceName', deviceName);
        }

        if (!isProject && !isEditMode && requestedProjectId) {
            formController.rhfProps.setValue('project', requestedProjectId);
        }
    }, [formController.rhfProps, isEditMode, isProject, requestedProjectId, searchParams]);

    return swarmType ? (
        <BasePage
            title={title}
            wrapMainContainer={false}
            besideTitle={
                <ButtonContainer>
                    {!isProject && (
                        <Button ref={detachButtonRef} hidden={!showSaveButton}>
                            {t('Detach')}
                        </Button>
                    )}
                    {!isProject && (
                        <OpenProjectButton formController={formController} swarmType={swarmType} />
                    )}
                    {showSaveButton && (
                        <>
                            <SaveLoadingButton
                                formController={formController}
                                loadingKey="top-save"
                                onClick={(e) => {
                                    formController.triggerSubmit(e, {
                                        saveButtonKey: 'top-save',
                                    });
                                }}
                                id="save-form"
                            />
                            {!isEditMode && !isProject && (
                                <SaveLoadingButton
                                    formController={formController}
                                    loadingKey="top-save-and-new"
                                    onClick={(e) => {
                                        formController.triggerSubmit(e, {
                                            addAnotherAfterSave: true,
                                            saveButtonKey: 'top-save-and-new',
                                        });
                                    }}
                                    id="save-and-add-another-form"
                                    variant="mutedPrimary"
                                >
                                    {gettext('SAVE_AND_ADD_ANOTHER')}
                                </SaveLoadingButton>
                            )}
                        </>
                    )}
                </ButtonContainer>
            }
        >
            <Form
                formController={formController}
                InjectableComponent={InjectableComponent}
                DataLink={GraphQLDataLink}
                showSaveButton={showSaveButton}
                saveButtonLoadingKey="bottom-save"
                additionalSaveAction={
                    !isEditMode && !isProject ? (
                        <SaveLoadingButton
                            type="button"
                            formController={formController}
                            loadingKey="bottom-save-and-new"
                            onClick={(e) => {
                                formController.triggerSubmit(e, {
                                    addAnotherAfterSave: true,
                                    saveButtonKey: 'bottom-save-and-new',
                                });
                            }}
                            variant="mutedPrimary"
                        >
                            {gettext('SAVE_AND_ADD_ANOTHER')}
                        </SaveLoadingButton>
                    ) : null
                }
            >
                <Layout formController={formController} />
            </Form>
        </BasePage>
    ) : (
        <div className="main-container-new">{gettext('LOADING')}</div>
    );
}

GenericForm.propTypes = {
    swarmType: PropTypes.oneOf(Object.values(SwarmType)).isRequired,
    isProject: PropTypes.bool.isRequired,
};

export default GenericForm;
